import React, { useState } from 'react';
import data from '../data/portfolio.json';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { useGSAP } from "@gsap/react";

// Register required GSAP plugins
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

export function Getdata() {
  // --------------------------------------------------
  // STATE MANAGEMENT
  // --------------------------------------------------
  const [scrollPosition, setScrollPosition] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return sessionStorage.getItem('portCat') || 'All';
  });

  // Filter and sort portfolio data
  const filteredData = data
    .filter(item => selectedCategory === 'All' ? true : item.design_category === selectedCategory)
    .sort((a, b) => b.design_id - a.design_id);

  // Updated to accept the category string directly from button clicks
  const handleCategoryChange = (newCategory) => {
    setSelectedCategory(newCategory);
    sessionStorage.setItem('portCat', newCategory);
  };

  // ===========================
  // THUMBNAIL REVEAL SCRIPT
  // ===========================
  function ThumbScrollFX() {
    gsap.set(".thumbnail-frame", { opacity: 0, rotationY: 20, rotationX: -30, perspective: 400 });
    gsap.set(".thumbnail-heading", { opacity: 0 });
    
    let thumb = document.querySelectorAll('.thumbnail');  
    
    ScrollTrigger.batch(thumb, {
      start: 'top 85%',
      once: true,
      onEnter: (batch) => {
        const frames = batch.map(section => section.querySelector('.thumbnail-frame'));
        const headings = batch.map(section => section.querySelector('.thumbnail-heading'));

        let tl_folder = gsap.timeline();

        tl_folder.to(frames, {
          duration: 1.2, 
          opacity: 1, 
          rotationY: 0, 
          rotationX: 0, 
          transformOrigin: "center center",  
          ease: "power2.out",
          stagger: 0.15 
        })
        .to(headings, { 
          duration: 0.5, 
          opacity: 1,
          stagger: 0.15 
        }, "<+=0.2");
      }
    });

    setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);
  }

  // --------------------------------------------------
  // GSAP TRIGGER ON CATEGORY CHANGE - FILTER
  // --------------------------------------------------
  useGSAP(() => {
    const containers = document.querySelectorAll(".thumbnail-image");

    containers.forEach((container) => {
      const image = container.querySelector("img");
      if (!image) return;

      const onEnter = () => {
        gsap.to(image, { scale: 1.2, duration: 0.5, ease: "power1.out", overwrite: "auto" });
      };

      const onLeave = () => {
        gsap.to(image, { scale: 1, duration: 0.5, ease: "power1.out", overwrite: "auto" });
      };

      container.addEventListener("mouseenter", onEnter);
      container.addEventListener("mouseleave", onLeave);

      return () => {
        container.removeEventListener("mouseenter", onEnter);
        container.removeEventListener("mouseleave", onLeave);
      };
    });

    gsap.set(".thumbnail-frame", { opacity: 0, rotationY: 20, rotationX: -30, perspective: 400 });
    gsap.set(".thumbnail-heading", { opacity: 0 });

    let thumb = document.querySelectorAll('.thumbnail');  
    ScrollTrigger.batch(thumb, {
      start: 'center 85%',
      once: true,
      onEnter: (batch) => {
        const frames = batch.map(section => section.querySelector('.thumbnail-frame'));
        const headings = batch.map(section => section.querySelector('.thumbnail-heading'));

        let tl_folder = gsap.timeline();

        tl_folder.to(frames, {
          duration: 1.2, 
          opacity: 1, 
          rotationY: 0, 
          rotationX: 0, 
          transformOrigin: "center center",  
          ease: "power2.out",
          stagger: 0.15 
        })
        .to(headings, { 
          duration: 0.5, 
          opacity: 1,
          stagger: 0.15 
        }, "<+=0.2");
      }
    });

  }, [selectedCategory]);

  // --------------------------------------------------
  // AUTOSCROLL BACK TO POSITION
  // --------------------------------------------------
  useGSAP(() => {
    const savedPosition = sessionStorage.getItem('scrollPos');

    const handleScroll = () => {
      const currentPosition = window.scrollY;
      sessionStorage.setItem('scrollPos', currentPosition);
    };

    if (savedPosition) {
      const targetY = parseFloat(savedPosition);
      const distance = Math.abs(window.scrollY - targetY);
      const pixelsPerSecond = 2000;
      const minDuration = 1;       
      const maxDuration = 4.5;       

      const dynamicDuration = Math.min(
        Math.max(distance / pixelsPerSecond, minDuration),
        maxDuration
      );

      gsap.to(window, { 
        scrollTo: targetY, 
        duration: dynamicDuration,
        ease: "power2.out",
        delay: 2,
        onComplete: () => {
          window.addEventListener('scroll', handleScroll, { passive: true });
        }
      });
    } else {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------
  return (
    <div className="gallery">

      <div className="portfolio-filter">

        <strong>Filter By Design Category</strong>
        <div className="filter-button-group">
          <button id="f-btn-1"  
            type="button"
            className={selectedCategory === 'All' ? 'active' : ''}
            onClick={() => handleCategoryChange('All')}>
            All
          </button>
          <button id="f-btn-2" 
            type="button"
            className={selectedCategory === 'Graphic Design' ? 'active' : ''}
            onClick={() => handleCategoryChange('Graphic Design')} >
            Graphic Design
          </button>
          <button id="f-btn-3" 
            type="button"
            className={selectedCategory === 'Web Development' ? 'active' : ''}
            onClick={() => handleCategoryChange('Web Development')} >
            Web Development
          </button>
        </div>

  


      </div>

      <div className="thumbnail-wrap">
        {filteredData.map(folder => (
          <Link 
            className="thumbnail" 
            to={`/portfolio/${folder.design_slug + '-id-' + folder.design_id}`} 
            key={folder.design_id} 
            data-port-id={folder.design_id}> 
            <div className="thumbnail-frame">
              <div className="thumbnail-image">
                <img src={folder.design_thumbnail} alt={folder.design_type + " id " + folder.design_id} loading="lazy" />
              </div>
              <div className="border-fx-top"></div>
              <div className="border-fx-right"></div>
              <div className="border-fx-bottom"></div>
              <div className="border-fx-left"></div>
            </div>
            <div className="thumbnail-heading">
               <span className="thumb-client">{folder.design_client}</span> - <span className="thumb-catrgory-and-date">{folder.design_type}</span>
            </div>
          </Link> 
        ))}     
      </div>
    </div>
  );
}